import React, {useState} from 'react';
import {Input, Form, Row, Col, Select} from "antd";
import {DIALOG_KEY} from "../../shared/utils/enum";
import toast from "react-hot-toast";
import {CancelButton, DeleteButton, SaveButton} from "../../shared/components/buttons/CustomButtons";
import {useSaveMiniGame, useDeleteMiniGame} from "./characterPackage/useCharacterPackage";
import {MiniGameEntity} from "./characterPackage/MiniGameEntity";
import {useMiniGameData} from "../logs/useTableData";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";

const getCardInfoGroupIdList = (data) => {
    const groupIdSet = new Set();
    Object.values(data).forEach(item => {
        if (item.cardGroupId) {
            groupIdSet.add(item.cardGroupId);
        }
    });
    return Array.from(groupIdSet);
}

const getGameInfoIdxList = (data) => {
    const infoIdxSet = new Set();
    Object.values(data).forEach(item => {
        if (item.index) {
            infoIdxSet.add(item.index);
        }
    });
    return Array.from(infoIdxSet);
}

const getMissionGroupIdList = (data) => {
    const missionGroupIdSet = new Set();
    Object.values(data).forEach(item => {
        if (item.missionGroupId) {
            missionGroupIdSet.add(item.missionGroupId);
        }
    });
    return Array.from(missionGroupIdSet);
}

const getRewardGroupIdList = (data) => {
    const rewardGroupIdSet = new Set();
    Object.values(data).forEach(item => {
        if (item.rewardGroupId) {
            rewardGroupIdSet.add(item.rewardGroupId);
        }
    });
    return Array.from(rewardGroupIdSet);
}

const MiniGameEditModal = ({record, handleClose, dialogRef, handleSetDialog}) => {
    const {mutate: saveMiniGame, isPending: isSavePending} = useSaveMiniGame();
    const {mutate: deleteMiniGame, isPending: isDeletePending} = useDeleteMiniGame();
    const {data: miniGameData, isLoading: isMiniGameDataLoading, error: miniGameDataError} = useMiniGameData();

    const isPending = isSavePending || isDeletePending;

    const [row, setRow] = useState(new MiniGameEntity(record));


    const handleSetRows = (key, value) => {
        setRow((prevState) => {
            const newState = {...prevState};
            newState[key] = value;
            return new MiniGameEntity(newState);
        });
    }

    const saveRowData = async (rowData) => {
        await saveMiniGame(rowData, {
            onSuccess: (res) => {
                toast.dismiss();
                toast.success('저장 성공');
                handleClose(true);
            },
            onError: (err) => {
                toast.dismiss();
                console.error(err);
                toast.error("저장 실패");
            }
        });
    }

    const handleSave = async (e) => {
        e.preventDefault();
        if (row.isInvalidate()) return;

        await saveRowData(row.toJson());
        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
    }

    const handleDelete = async (e) => {
        e.preventDefault();
        const currentDialog = dialogRef.current;  // ref에서 최신 상태 가져오기
        if (currentDialog.confirmText === 'DELETE') {

            await deleteMiniGame(row.seq, {
                onSuccess: (res) => {
                    toast.dismiss();
                    toast.success('삭제 성공');
                    handleClose(true);
                },
                onError: (err) => {
                    toast.dismiss();
                    console.error(err);
                    toast.error("삭제 실패");
                }
            });
            handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
        } else {
            toast.error("잘못된 확인 문자입니다. DELETE 를 입력하세요.");
            handleSetDialog([{key: DIALOG_KEY.CONFIRM_TEXT, value: ''}]);
        }
    }

    if (isPending) {
        toast.loading('저장 중...');
    }

    if (isMiniGameDataLoading) return <IsLoading />;
    if (miniGameDataError) return <ErrorPage error={miniGameDataError} />;

    return (
        <div>
            <div style={{marginTop: 26}}>
                <Form layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="인덱스">
                                <Input
                                    type="number"
                                    name={"index"}
                                    value={row.index}
                                    onChange={(e) => handleSetRows('index', parseInt(e.target.value) || 0)}
                                    size="small"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="CardInfo 인덱스">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="CardInfo 인덱스 선택"
                                    value={row.cardInfo}
                                    onChange={value => handleSetRows('cardInfo', value)}
                                    size="small"
                                    optionFilterProp="children"
                                    filterOption={(input, option) => option.children.toString().includes(input)}
                                >
                                    {getCardInfoGroupIdList(miniGameData.miniGameCardInfo || []).map(id => (
                                        <Select.Option key={id} value={Number(id)}>{id}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="GameInfo 인덱스">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="GameInfo 인덱스 선택"
                                    value={row.info}
                                    onChange={value => handleSetRows('info', value)}
                                    size="small"
                                    optionFilterProp="children"
                                    filterOption={(input, option) => option.children.toString().includes(input)}
                                >
                                    {getGameInfoIdxList(miniGameData.miniGameInfo || []).map(id => (
                                        <Select.Option key={id} value={Number(id)}>{id}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Mission 인덱스">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Mission 인덱스 선택"
                                    value={row.mission}
                                    onChange={value => handleSetRows('mission', value)}
                                    size="small"
                                    optionFilterProp="children"
                                    filterOption={(input, option) => option.children.toString().includes(input)}
                                >
                                    {getMissionGroupIdList(miniGameData.miniGameMission || []).map(id => (
                                        <Select.Option key={id} value={Number(id)}>{id}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Reward 인덱스">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Reward 인덱스 선택"
                                    value={row.reward}
                                    onChange={value => handleSetRows('reward', value)}
                                    size="small"
                                    optionFilterProp="children"
                                    filterOption={(input, option) => option.children.toString().includes(input)}
                                >
                                    {getRewardGroupIdList(miniGameData.miniGameReward || []).map(id => (
                                        <Select.Option key={id} value={Number(id)}>{id}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item label="메모">
                                <Input
                                    name={"memo"}
                                    value={row.memo}
                                    onChange={(e) => handleSetRows('memo', e.target.value)}
                                    size="small"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </div>
            <div className={"event-edit-buttons"}>
                <SaveButton onClick={handleSave} handleSetDialog={handleSetDialog}/>
                <CancelButton onClick={handleClose}/>
                <DeleteButton onClick={handleDelete} handleSetDialog={handleSetDialog} display={false}/>
            </div>
        </div>
    );
}

export default MiniGameEditModal;